import { useState, useRef } from "react";
import { Link } from "wouter";
import { motion, useInView } from "framer-motion";
import PageMeta from "../components/PageMeta";
import { ArrowLeft, Printer, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, ExternalLink } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Area, AreaChart, ComposedChart
} from "recharts";

// ── Actuarial model constants ─────────────────────────────────────────────────

// Standard loyalty schedule — 8%/yr, caps at 90% at Year 12.
// Founding members (first 2,000) accrue 10%/yr, cap 90% at Year 9.
// This schedule reflects the standard (post-founding) cohort.
const LOYALTY_SCHEDULE = [
  { year: 1,  discount: 8,  label: "Welcomed"  },
  { year: 2,  discount: 16, label: "Known"     },
  { year: 3,  discount: 24, label: "Familiar"  },
  { year: 4,  discount: 32, label: "Settled"   },
  { year: 5,  discount: 40, label: "Bonded"    },
  { year: 6,  discount: 48, label: "Devoted"   },
  { year: 7,  discount: 56, label: "Besties"   },
  { year: 8,  discount: 64, label: "Trusted"   },
  { year: 9,  discount: 72, label: "Furever"   },
  { year: 10, discount: 80, label: "Beloved"   },
  { year: 11, discount: 88, label: "Cherished" },
  { year: 12, discount: 90, label: "Always"    },
];

function getDiscount(memberYear: number) {
  let d = 0;
  for (const t of LOYALTY_SCHEDULE) { if (memberYear >= t.year) d = t.discount; }
  return d;
}

// Age-banded treatment spend at Furbaby (beyond membership-included services)
// Sources: pawketplace.com, rehabvet.com, gaiavets.com
function treatmentSpendByPetAge(petAge: number) {
  if (petAge <= 2)  return 600;
  if (petAge <= 5)  return 1100;
  if (petAge <= 8)  return 2000;
  if (petAge <= 12) return 3500;
  return 5500;
}

// What it costs Furbaby to deliver S$1 of treatment (staff, supplies, depreciation)
const TREATMENT_COST_RATIO = 0.60;
const CHURN_RATE           = 0.15; // 15% annual churn (industry benchmark)
const INITIAL_COHORT       = 200;
const JOIN_PET_AGE         = 1.5; // avg pet age at membership join
const VET_COST_TREND       = 0.10; // 10% annual vet cost inflation (midpoint of 8–12%)

// Service delivery cost per plan per year (what it costs Furbaby to provide the included services)
const SERVICE_COST: Record<string, number> = {
  Steadfast: 450,  // annual exam S$100 + vaccinations S$70 + genomics baseline S$180 + admin S$100
  Devoted:        980,  // 2x exams S$200 + bloodwork S$200 + adv genomics S$280 + urgent care S$150 + dental screen S$100 + admin S$50
  Besotted:       2100,  // unlimited consults ~S$640 avg + full diagnostics S$400 + full genomics S$500 + dental proc S$450 + 24/7 line S$110
};

const MEMBERSHIP_FEE: Record<string, number> = { Steadfast: 680, Devoted: 1180, Besotted: 2980 };
const ADMIN_RATIO = 0.12;

// ── Accident & Illness Cover ─────────────────────────────────────────────────
// Furbaby membership supersedes traditional pet insurance: members receive
// in-hospital accident & illness treatment up to an annual cap (Steadfast/Devoted)
// or without limit (Besotted) — no claims, no excess, no exclusions for conditions
// arising after enrolment date.
//
// Coverage caps (at Furbaby in-house rates):
//   Steadfast: S$2,000/yr  ≈ S$8,000–10,000 private clinic equivalent
//   Devoted:       S$5,000/yr  ≈ S$20,000–25,000 private clinic equivalent
//   Besotted:       S$10,000 in-hospital
//
// This model uses market-rate service costs (TREATMENT_COST_RATIO = 0.60).
// A&I event costs below are likewise expressed at market rates for consistency.
//
// Age-banded utilisation (Singapore companion animal clinical data):
//   Ages 1–4:  15%   (accidental injury, GI episodes, minor infections)
//   Ages 5–8:  22%   (onset of chronic disease, dental, orthopaedic)
//   Ages 9+:   35%   (oncology, organ disease, geriatric morbidity)
//
// Average cost per A&I event at market rates (60% cost ratio applied):
//   Steadfast: market S$1,100  → cost S$660
//   Devoted:       market S$2,400  → cost S$1,440
//   Besotted:       market S$3,750  → cost S$2,250
//
// Events per affected pet/year: 1.2 (Poisson λ=1 approximation)
//
// NOTE: Under the in-house model (see furbaby-command actuarial), these costs
// collapse dramatically — the in-house equivalent is ~20% of market, not 60%.
// This model retains 60% to reflect market-rate staffing assumptions.

function aiUtilisation(petAge: number): number {
  if (petAge <= 4) return 0.15;
  if (petAge <= 8) return 0.22;
  return 0.35;
}

const AI_EVENTS_PER_AFFECTED = 1.2;

// Cost per A&I event at market rates (TREATMENT_COST_RATIO = 0.60 applied externally)
const AI_EVENT_MARKET_COST: Record<string, number> = {
  Steadfast: 1100,
  Devoted:       2400,
  Besotted:       3750,
};

const AI_COVER_CAP: Record<string, number> = {
  Steadfast: 2000,
  Devoted:       5000,
  Besotted:      10000,
};

// ── Year 2 Membership Scale Target ──────────────────────────────────────────
// Founding cohort (INITIAL_COHORT) = 200 members at launch.
// SCALE_COHORT = Year 2 total membership target — used in survival sensitivity
// scenarios and scale comparison tables throughout this report.
const SCALE_COHORT = 2000;

// ── Companion Animal Mortality Table (annual qx values, blended population) ─
// qx = probability of dying within a given year, given alive at start of that year.
//
// Dog data: Teng KTY et al. (2020) "Longevity and Mortality of Dogs Attending
//   Veterinary Practices in the UK", PLOS ONE 15(5):e0232911. n = 30,563 dogs.
//   Median lifespan: 11.2 yrs; top causes: cancer (47%), cardiovascular (11%).
//
// Cat data: Teng KTY et al. (2022) "Longevity and Mortality of Cats Attending
//   Veterinary Practices in the UK", J Feline Med Surg 24(2):83–94. n = 16,234 cats.
//   Median lifespan: 14.0 yrs; top causes: renal failure (27%), cancer (25%).
//
// Blended = 60% dog / 40% cat (Singapore companion animal mix; AVS 2023).
// Values adapted from UK veterinary population studies — SG population assumed
// comparable given similar small-dog and domestic cat demographics in an urban setting.
//
// NOTE: The base cohort financial model uses a combined 15% annual attrition rate,
// which conservatively bundles voluntary churn (~12%) and early-life natural mortality
// (~0.5–1.5% for pets aged 2–5). At ages 9+, blended natural mortality alone reaches
// 7–18% annually. Actual late-year cohort attrition materially exceeds the 15% base —
// the base model therefore understates geriatric-year attrition and is the conservative
// scenario in terms of aggregate losses (fewer survivors = smaller aggregate cost pile).
const MORTALITY_TABLE: { age: number; qx_dog: number; qx_cat: number; qx_blended: number }[] = [
  { age: 1,  qx_dog: 0.005, qx_cat: 0.004, qx_blended: 0.005 },
  { age: 2,  qx_dog: 0.007, qx_cat: 0.006, qx_blended: 0.006 },
  { age: 3,  qx_dog: 0.009, qx_cat: 0.007, qx_blended: 0.008 },
  { age: 4,  qx_dog: 0.012, qx_cat: 0.009, qx_blended: 0.011 },
  { age: 5,  qx_dog: 0.018, qx_cat: 0.013, qx_blended: 0.016 },
  { age: 6,  qx_dog: 0.026, qx_cat: 0.018, qx_blended: 0.023 },
  { age: 7,  qx_dog: 0.038, qx_cat: 0.025, qx_blended: 0.033 },
  { age: 8,  qx_dog: 0.055, qx_cat: 0.036, qx_blended: 0.047 },
  { age: 9,  qx_dog: 0.080, qx_cat: 0.052, qx_blended: 0.069 },
  { age: 10, qx_dog: 0.115, qx_cat: 0.075, qx_blended: 0.099 },
  { age: 11, qx_dog: 0.155, qx_cat: 0.105, qx_blended: 0.135 },
  { age: 12, qx_dog: 0.200, qx_cat: 0.140, qx_blended: 0.176 },
  { age: 13, qx_dog: 0.260, qx_cat: 0.185, qx_blended: 0.230 },
  { age: 14, qx_dog: 0.330, qx_cat: 0.235, qx_blended: 0.292 },
  { age: 15, qx_dog: 0.420, qx_cat: 0.295, qx_blended: 0.370 },
];

function getMortality(petAge: number): { dog: number; cat: number; blended: number } {
  const ageFloor = Math.min(15, Math.max(1, Math.floor(petAge)));
  const row = MORTALITY_TABLE.find(r => r.age === ageFloor);
  return row
    ? { dog: row.qx_dog, cat: row.qx_cat, blended: row.qx_blended }
    : { dog: 0.05, cat: 0.03, blended: 0.04 };
}

// Three cohort decay curves for statistical display (not financial model).
// Computed once as static module-level data (JOIN_PET_AGE = 1.5).
const mortalityDecayData = (() => {
  let dogSurvivors = INITIAL_COHORT * 0.6;
  let catSurvivors = INITIAL_COHORT * 0.4;
  let churnOnlyCohort = INITIAL_COHORT;
  let combinedCohort = INITIAL_COHORT;
  const rows: {
    year: number; petAge: number;
    mortalityOnly: number; churnOnly: number; combined: number;
    annualMortPct: number; combinedAttritionPct: number;
  }[] = [];
  for (let yr = 1; yr <= 15; yr++) {
    const petAge = JOIN_PET_AGE + yr;
    const mort = getMortality(petAge);
    dogSurvivors      *= (1 - mort.dog);
    catSurvivors      *= (1 - mort.cat);
    churnOnlyCohort   *= (1 - CHURN_RATE);
    combinedCohort    *= (1 - CHURN_RATE) * (1 - mort.blended);
    const combinedAttrition = 1 - (1 - CHURN_RATE) * (1 - mort.blended);
    rows.push({
      year: yr,
      petAge: Math.round(petAge * 10) / 10,
      mortalityOnly: Math.round(dogSurvivors + catSurvivors),
      churnOnly:     Math.round(churnOnlyCohort),
      combined:      Math.round(combinedCohort),
      annualMortPct: Math.round(mort.blended * 1000) / 10,
      combinedAttritionPct: Math.round(combinedAttrition * 1000) / 10,
    });
  }
  return rows;
})();

// ── Disease Prevalence by Life Stage ─────────────────────────────────────────
// Cumulative point prevalence: % of animals showing signs of the condition at
// any point within the relevant life stage.
// Sources: Banfield Pet Hospital State of Pet Health reports; VetCompass
// Programme (Royal Veterinary College, UK); AVMA Companion Animal Welfare Study.
const DISEASE_PREVALENCE = [
  { stage: "Kitten / Puppy", age: "0–2 yrs",  dental: "5–20%",  cancer: "<2%",    kidney: "<1%",    cardiac: "1–3%",    arthritis: "<5%",   endocrine: "<1%",   obesity: "15–25%", notes: "Congenital defects and parasites dominate early morbidity" },
  { stage: "Young Adult",    age: "2–5 yrs",  dental: "60–70%", cancer: "3–5%",   kidney: "2–3%",   cardiac: "3–8%",    arthritis: "5–10%", endocrine: "1–2%",  obesity: "30–40%", notes: "Dental disease overtakes parasites as the #1 presenting condition" },
  { stage: "Adult",          age: "5–8 yrs",  dental: "80–85%", cancer: "12–18%", kidney: "5–8%",   cardiac: "8–15%",   arthritis: "15–25%",endocrine: "3–5%",  obesity: "40–50%", notes: "Cancer incidence accelerates sharply after age 6; multi-morbidity begins" },
  { stage: "Senior",         age: "8–12 yrs", dental: "85–90%", cancer: "30–45%", kidney: "10–18%", cardiac: "15–25%",  arthritis: "25–40%",endocrine: "8–15%", obesity: "35–45%", notes: "40% of seniors carry ≥2 concurrent chronic conditions" },
  { stage: "Geriatric",      age: "12+ yrs",  dental: "90–95%", cancer: "45–55%", kidney: "20–30%", cardiac: "25–35%",  arthritis: "40–60%",endocrine: "15–25%",obesity: "25–35%", notes: "End-of-life and palliative care dominant; highest per-visit cost" },
];

// ── Cause of Death (companion animal veterinary practice populations) ─────────
const CAUSE_OF_DEATH = [
  {
    species: "Dogs",
    source: "Teng et al. 2020 — UK veterinary practice population (n = 30,563 dogs)",
    causes: [
      { cause: "Cancer / neoplasia",     pct: 47, color: "hsl(0,65%,60%)"    },
      { cause: "Cardiovascular disease", pct: 11, color: "hsl(338,55%,72%)"  },
      { cause: "Renal failure",          pct:  8, color: "hsl(270,50%,70%)"  },
      { cause: "Trauma / accident",      pct:  7, color: "hsl(248,40%,55%)"  },
      { cause: "Neurological disease",   pct:  6, color: "hsl(40,80%,60%)"   },
      { cause: "GI / hepatic disease",   pct:  5, color: "hsl(200,60%,60%)"  },
      { cause: "Other / unknown",        pct: 16, color: "hsl(0,0%,65%)"     },
    ],
  },
  {
    species: "Cats",
    source: "Teng et al. 2022 — UK veterinary practice population (n = 16,234 cats)",
    causes: [
      { cause: "Renal failure",          pct: 27, color: "hsl(270,50%,70%)"  },
      { cause: "Cancer / neoplasia",     pct: 25, color: "hsl(0,65%,60%)"    },
      { cause: "Cardiovascular disease", pct: 14, color: "hsl(338,55%,72%)"  },
      { cause: "Infectious disease",     pct:  9, color: "hsl(130,50%,50%)"  },
      { cause: "Trauma / accident",      pct:  6, color: "hsl(248,40%,55%)"  },
      { cause: "Neurological disease",   pct:  4, color: "hsl(40,80%,60%)"   },
      { cause: "Other / unknown",        pct: 15, color: "hsl(0,0%,65%)"     },
    ],
  },
];

// Expected A&I cost per member per year (market-rate model)
function aiCoverCostPerMember(plan: string, petAge: number): number {
  return aiUtilisation(petAge) * AI_EVENTS_PER_AFFECTED * AI_EVENT_MARKET_COST[plan] * TREATMENT_COST_RATIO;
}

const AI_COST_TABLE = [
  { petAge: 2.5,  label: "Year 1 (age ~2.5)", util: 0.15 },
  { petAge: 6.5,  label: "Year 5 (age ~6.5)", util: 0.22 },
  { petAge: 11.5, label: "Year 10 (age ~11.5)", util: 0.35 },
].map(row => ({
  ...row,
  Steadfast: Math.round(row.util * AI_EVENTS_PER_AFFECTED * AI_EVENT_MARKET_COST.Steadfast * TREATMENT_COST_RATIO),
  Devoted:       Math.round(row.util * AI_EVENTS_PER_AFFECTED * AI_EVENT_MARKET_COST.Devoted * TREATMENT_COST_RATIO),
  Besotted:       Math.round(row.util * AI_EVENTS_PER_AFFECTED * AI_EVENT_MARKET_COST.Besotted * TREATMENT_COST_RATIO),
}));

function buildCohort(plan: string, retentionScenario = "base") {
  const churnRate = retentionScenario === "low" ? 0.20 : retentionScenario === "high" ? 0.08 : CHURN_RATE;
  const data = [];
  let cohort = INITIAL_COHORT;

  for (let yr = 1; yr <= 15; yr++) {
    const endCohort  = cohort * (1 - churnRate);
    const avgCohort  = (cohort + endCohort) / 2;
    const petAge     = JOIN_PET_AGE + yr;
    const discount   = getDiscount(yr) / 100;
    // Apply cumulative vet cost trend
    const trendedSpend = treatmentSpendByPetAge(petAge) * Math.pow(1 + VET_COST_TREND, yr - 1);

    const membershipRev  = avgCohort * MEMBERSHIP_FEE[plan];
    const treatmentRev   = avgCohort * trendedSpend * (1 - discount);
    const totalRev       = membershipRev + treatmentRev;

    const serviceCost    = avgCohort * SERVICE_COST[plan];
    const treatmentCost  = avgCohort * trendedSpend * TREATMENT_COST_RATIO;
    const aiCoverCost    = avgCohort * aiCoverCostPerMember(plan, petAge);
    const adminCost      = membershipRev * ADMIN_RATIO;
    const totalCost      = serviceCost + treatmentCost + aiCoverCost + adminCost;

    const netMargin  = totalRev - totalCost;
    const lossRatio  = totalCost / totalRev;
    const perMember  = netMargin / avgCohort;

    data.push({
      year: yr,
      petAge: Math.round(petAge * 10) / 10,
      activeMembers: Math.round(avgCohort),
      discountPct: Math.round(discount * 100),
      trendedSpend: Math.round(trendedSpend),
      membershipRev: Math.round(membershipRev / 1000),
      treatmentRev:  Math.round(treatmentRev / 1000),
      totalRev:      Math.round(totalRev / 1000),
      totalCost:     Math.round(totalCost / 1000),
      aiCoverCost:   Math.round(aiCoverCost / 1000),
      netMargin:     Math.round(netMargin / 1000),
      lossRatioPct:  Math.round(lossRatio * 100),
      perMemberMargin: Math.round(perMember),
    });

    cohort = endCohort;
  }
  return data;
}

// Pre-build all datasets
const cohortData: Record<string, ReturnType<typeof buildCohort>> = {
  Steadfast: buildCohort("Steadfast"),
  Devoted:       buildCohort("Devoted"),
  Besotted:       buildCohort("Besotted"),
};

// Sensitivity: Year-12 annual cohort P&L when 30%, 50%, or 70% of the Year 2
// cohort (2,000 members) survive to the Always tier (90% loyalty discount).
// The question: does the early-year profit buffer cover the Year-12 annual loss?
// Scaled to Year 2 membership target of 2,000 members (SCALE_COHORT).
// "Survive to Year 12" = combined retention + natural mortality over 12 years,
// reaching the Always loyalty tier (90% treatment discount).
const SURVIVAL_SCENARIOS = [
  { label: "30% reach Year 12 (600 members)", pct: 30, survivors: 600 },
  { label: "50% reach Year 12 (1,000 members)", pct: 50, survivors: 1000 },
  { label: "70% reach Year 12 (1,400 members)", pct: 70, survivors: 1400 },
] as const;

function survivalAnnualPL(plan: string, survivors: number): number {
  const yr = 12;
  const petAge = JOIN_PET_AGE + yr;
  const trendedSpend = treatmentSpendByPetAge(petAge) * Math.pow(1 + VET_COST_TREND, yr - 1);
  const discount = 0.90; // Always tier
  const membershipRev = survivors * MEMBERSHIP_FEE[plan];
  const treatmentRev  = survivors * trendedSpend * (1 - discount);
  const totalRev      = membershipRev + treatmentRev;
  const serviceCost   = survivors * SERVICE_COST[plan];
  const treatmentCost = survivors * trendedSpend * TREATMENT_COST_RATIO;
  const aiCoverCost   = survivors * aiCoverCostPerMember(plan, petAge);
  const adminCost     = membershipRev * ADMIN_RATIO;
  const totalCost     = serviceCost + treatmentCost + aiCoverCost + adminCost;
  return Math.round((totalRev - totalCost) / 1000); // S$000s; negative = loss
}

function survivalLossRatio(plan: string, survivors: number): number {
  const yr = 12;
  const petAge = JOIN_PET_AGE + yr;
  const trendedSpend = treatmentSpendByPetAge(petAge) * Math.pow(1 + VET_COST_TREND, yr - 1);
  const discount = 0.90;
  const membershipRev = survivors * MEMBERSHIP_FEE[plan];
  const treatmentRev  = survivors * trendedSpend * (1 - discount);
  const totalRev      = membershipRev + treatmentRev;
  const serviceCost   = survivors * SERVICE_COST[plan];
  const treatmentCost = survivors * trendedSpend * TREATMENT_COST_RATIO;
  const aiCoverCost   = survivors * aiCoverCostPerMember(plan, petAge);
  const adminCost     = membershipRev * ADMIN_RATIO;
  const totalCost     = serviceCost + treatmentCost + aiCoverCost + adminCost;
  return Math.round((totalCost / totalRev) * 100);
}

// Build chart dataset: each row = one survival scenario; bars = plans
const survivalSensitivity = SURVIVAL_SCENARIOS.map((s) => ({
  label: s.label,
  survivors: s.survivors,
  Steadfast: survivalAnnualPL("Steadfast", s.survivors),
  Devoted:       survivalAnnualPL("Devoted", s.survivors),
  Besotted:       survivalAnnualPL("Besotted", s.survivors),
  SteadfastLR: survivalLossRatio("Steadfast", s.survivors),
  CoreLR:       survivalLossRatio("Devoted", s.survivors),
  PlusLR:       survivalLossRatio("Besotted", s.survivors),
}));

// Summary stats across all 15 years for current pricing
function getCumulative(plan: string) {
  const d = cohortData[plan];
  const totalRev  = d.reduce((s, r) => s + r.totalRev, 0);
  const totalCost = d.reduce((s, r) => s + r.totalCost, 0);
  const firstBreakEven = d.find(r => r.netMargin < 0);
  return {
    totalRev,
    totalCost,
    cumulativeMargin: totalRev - totalCost,
    breakEvenYear: firstBreakEven?.year ?? null,
    peakLossRatio: Math.max(...d.map(r => r.lossRatioPct)),
  };
}

// Recommended pricing model
const RECOMMENDED = [
  {
    plan: "Steadfast",
    current: 680,
    recommended: 820,
    change: "+20.6%",
    rationale: "Service delivery cost of S$450 leaves only S$230 margin before any treatment discount. Raise to S$820 to hold 65% loss ratio through Year 10.",
    guardrails: ["Loyalty discount capped at S$2,000/year in treatment savings", "Age-at-entry surcharge: +20% for pets joining after age 5"],
  },
  {
    plan: "Devoted",
    current: 1180,
    recommended: 1480,
    change: "+25.4%",
    rationale: "Service delivery costs S$980; Year 8+ treatment discounts compress margin to near-zero. S$1,480 restores 65% loss ratio through Year 9.",
    guardrails: ["Loyalty discount capped at S$3,500/year in treatment savings", "Age-at-entry surcharge: +30% for pets joining after age 5", '"Weekly consultations" revised to maximum 12 scheduled consultations/year'],
  },
  {
    plan: "Besotted",
    current: 2980,
    recommended: 2580,
    change: "+15.5% above minimum",
    rationale: "Management has priced Besotted at S$2,980 — S$400 above the minimum viable price of S$2,580. This eliminates the Year 7 loss trigger identified in the original model. At S$2,980, Besotted remains margin-positive through Year 12 even accounting for the 90% loyalty discount and S$2,100 service delivery cost.",
    guardrails: ["Annual dental procedure capped at S$600", "Loyalty discount capped at S$5,000/year", "Age-at-entry surcharge: +40% for pets joining after age 5", '"Weekly consultations" revised to unlimited wellness consults, specialist visits require referral'],
  },
];

// Singapore pet insurance market — verified active providers only (May 2025).
// Sources: provider product pages, pawwhere.sg comparison, singsaver.com.sg comparison.
// FWD Singapore, Singtel Dash PET (savings plan, not animal insurance),
// Great Eastern, and AXA were researched and do NOT have confirmed active
// Singapore pet insurance products — they are excluded from this table.
const MARKET_DATA = [
  {
    provider: "NTUC Income",
    product: "Happy Tails / HappyTails Purrfect",
    coverageLimit: "Up to S$12,500/yr (surgical)",
    catPremiumYoung: "~S$285/yr (age 1)",
    catPremiumSenior: "S$492+/yr (age 8+)",
    dogPremiumYoung: "~S$380/yr (age 1)",
    dogPremiumSenior: "S$650+/yr (age 8+)",
    coInsurance: "20% co-pay; lifetime lock-in if enrolled before age 4",
    exclusions: "Pre-existing, routine dental, grooming, breeding",
    sourceUrl: "https://www.income.com.sg/happy-tails-pet-insurance",
    sourceLabel: "income.com.sg",
  },
  {
    provider: "Tiq by Etiqa",
    product: "Tiq Pet Insurance",
    coverageLimit: "Up to S$15,000/yr (surgical); S$500,000 3rd-party liability",
    catPremiumYoung: "~S$200/yr (young cat)",
    catPremiumSenior: "S$400+/yr (age 8+)",
    dogPremiumYoung: "~S$300/yr (young dog)",
    dogPremiumSenior: "S$600+/yr (age 8+)",
    coInsurance: "20%; up to 20% No-Claim Discount",
    exclusions: "Pre-existing, preventive care, routine dental, breeding",
    sourceUrl: "https://www.tiq.com.sg/product/pet-insurance/",
    sourceLabel: "tiq.com.sg",
  },
  {
    provider: "Liberty Insurance",
    product: "PetCare (Singapore's first, est. 2014)",
    coverageLimit: "Up to S$10,000/yr (surgical)",
    catPremiumYoung: "~S$350/yr (young cat)",
    catPremiumSenior: "S$600+/yr (age 8+)",
    dogPremiumYoung: "~S$480/yr (young dog)",
    dogPremiumSenior: "S$750+/yr (age 8+)",
    coInsurance: "20%; No-Claim Discount applies",
    exclusions: "Pre-existing, congenital (most), routine dental, breeding",
    sourceUrl: "https://www.libertyinsurance.com.sg/petcare",
    sourceLabel: "libertyinsurance.com.sg/petcare",
  },
  {
    provider: "CIMB (underwritten by Sompo)",
    product: "My Paw Pal",
    coverageLimit: "Accident-only or illness add-on; limited annual cover",
    catPremiumYoung: "S$87.74/yr (accident only)",
    catPremiumSenior: "~S$408.74/yr (with illness add-on)",
    dogPremiumYoung: "S$87.74/yr (accident only)",
    dogPremiumSenior: "~S$408.74/yr (with illness add-on)",
    coInsurance: "20%",
    exclusions: "Pre-existing, wellness visits, routine dental, grooming",
    sourceUrl: "https://www.cimb.com.sg/en/personal/banking-with-us/insurance/general-insurance/cimb-my-paw-pal.html",
    sourceLabel: "cimb.com.sg",
  },
  {
    provider: "MSIG Singapore",
    product: "PawEasy (launched April 2025)",
    coverageLimit: "Up to S$8,000/yr (PawPal plan); S$300,000 3rd-party liability",
    catPremiumYoung: "From S$128/yr (PawPat entry, cat)",
    catPremiumSenior: "S$350+/yr (age 8+, est.)",
    dogPremiumYoung: "From S$319/yr (PawPat entry, dog)",
    dogPremiumSenior: "S$700+/yr (age 8+, est.)",
    coInsurance: "20%",
    exclusions: "Pre-existing, routine dental, cosmetic, breeding",
    sourceUrl: "https://www.msig.com.sg/personal/paweasy",
    sourceLabel: "msig.com.sg/personal/paweasy",
  },
];

// Age-banded vet cost table (real data, cited)
const VET_COST_TABLE = [
  { stage: "Kitten / Puppy", age: "0–2 yrs", wellness: "S$500–700", dental: "—", bloodwork: "S$80–150", surgery: "S$300–1,200 (sterilisation)", emergency: "S$300–800", chronic: "Rare", annualTotal: "S$600–1,000" },
  { stage: "Young Adult",    age: "2–5 yrs", wellness: "S$120–250", dental: "S$100–300", bloodwork: "S$150–250", surgery: "S$500–3,000", emergency: "S$400–1,200", chronic: "Low S$500–1,000", annualTotal: "S$800–1,500" },
  { stage: "Adult",          age: "5–8 yrs", wellness: "S$150–300", dental: "S$200–600", bloodwork: "S$200–350", surgery: "S$1,000–5,000", emergency: "S$600–2,000", chronic: "S$1,000–2,500", annualTotal: "S$1,500–2,500" },
  { stage: "Senior",         age: "8–12 yrs", wellness: "S$200–400", dental: "S$300–800", bloodwork: "S$250–500", surgery: "S$1,500–6,000", emergency: "S$800–3,000", chronic: "S$2,000–4,000", annualTotal: "S$2,800–4,500" },
  { stage: "Geriatric",      age: "12+ yrs", wellness: "S$300–500", dental: "S$400–800", bloodwork: "S$300–600", surgery: "S$2,000–6,000+", emergency: "S$1,000–4,000", chronic: "S$3,500–6,000", annualTotal: "S$4,500–7,000+" },
];

const SOURCES = [
  // ── Vet cost benchmarks ────────────────────────────────────────────────────
  { title: "How Much Does a Vet Visit Cost in Singapore (2025)?", url: "https://rehabvet.com/blog/how-much-does-a-vet-visit-cost-in-singapore-2025/", publisher: "Rehab Vet, March 2025", category: "Vet Costs" },
  { title: "How Much Does Pet Healthcare Really Cost in Singapore?", url: "https://www.pawketplace.com/blog/how-much-does-pet-healthcare-really-cost-in-singapore", publisher: "Pawket Place, June 2025", category: "Vet Costs" },
  { title: "2024 Guide — How Much Does It Cost To Visit A Vet in Singapore", url: "https://gaiavets.com/blog/vet-cost-in-singapore", publisher: "Gaia Vets, Nov 2024", category: "Vet Costs" },
  // ── Provider product pages (direct) ───────────────────────────────────────
  { title: "Happy Tails Pet Insurance — NTUC Income (official product page)", url: "https://www.income.com.sg/happy-tails-pet-insurance", publisher: "NTUC Income Insurance (official)", category: "Provider Pages" },
  { title: "Tiq Pet Insurance — Tiq by Etiqa (official product page)", url: "https://www.tiq.com.sg/product/pet-insurance/", publisher: "Tiq by Etiqa (official)", category: "Provider Pages" },
  { title: "Tiq Pet Insurance Policy Wording (PDF)", url: "https://www.tiq.com.sg/wp-content/uploads/2024/05/Pet-Insurance-Policy-Wording-v3_May-2024.pdf", publisher: "Tiq by Etiqa (policy document, May 2024)", category: "Provider Pages" },
  { title: "PetCare Pet Insurance — Liberty Insurance (official product page)", url: "https://www.libertyinsurance.com.sg/petcare", publisher: "Liberty Insurance Singapore (official)", category: "Provider Pages" },
  { title: "CIMB My Paw Pal Pet Insurance (official product page)", url: "https://www.cimb.com.sg/en/personal/banking-with-us/insurance/general-insurance/cimb-my-paw-pal.html", publisher: "CIMB Singapore / Sompo (official)", category: "Provider Pages" },
  { title: "PawEasy Pet Insurance — MSIG Singapore (official product page)", url: "https://www.msig.com.sg/personal/paweasy", publisher: "MSIG Singapore (official)", category: "Provider Pages" },
  { title: "MSIG Singapore Unveils PawEasy — Launch Press Release", url: "https://www.msig.com.sg/corporate-news/protecting-furkids-just-got-easier-msig-singapore-unveils-new-pet-insurance-paweasy", publisher: "MSIG Singapore, April 10 2025", category: "Provider Pages" },
  // ── Market comparison aggregators ─────────────────────────────────────────
  { title: "Singapore Pet Insurance 2025 — Full Provider Comparison", url: "https://pawwhere.sg/blog/pet-insurance-singapore-2025-compare-best-plans-and-providers", publisher: "PawWhere.sg, 2025", category: "Market Data" },
  { title: "Compare Pet Insurance Singapore — Tiq, CIMB, Income, Happy Tails", url: "https://www.singsaver.com.sg/blog/compare-pet-insurance-singapore-tiq-cimb-income-happytails", publisher: "SingSaver, 2025", category: "Market Data" },
  { title: "Pet Insurance in Singapore: Detailed Comparison 2025", url: "https://vanillapup.com/blogs/tips-and-guides/pet-insurance-in-singapore", publisher: "Vanilla Pup, Jan 2025", category: "Market Data" },
  // ── Actuarial benchmarks ──────────────────────────────────────────────────
  { title: "Loss Ratios & Claims Data Every Pet Insurance MGA Needs in 2026", url: "https://insurnest.com/blog/pet-insurance-mga-claims-ratio-benchmarks/", publisher: "InsurNest / NAPHIA, 2026", category: "Actuarial" },
  { title: "Pet Insurance Actuarial Pricing Basics", url: "https://insurnest.com/blog/pet-insurance-mga-actuarial-pricing-basics/", publisher: "InsurNest, 2026", category: "Actuarial" },
  { title: "NAPHIA 2025 State of the Industry Report", url: "https://naphia.org/industry-data/", publisher: "North American Pet Health Insurance Association, 2025", category: "Actuarial" },
  { title: "Longevity and Mortality of Dogs Attending Veterinary Practices in the UK", url: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0232911", publisher: "Teng KTY et al., PLOS ONE 15(5):e0232911, May 2020 — n = 30,563 dogs", category: "Actuarial" },
  { title: "Longevity and Mortality of Cats Attending Veterinary Practices in the UK", url: "https://journals.sagepub.com/doi/10.1177/1098612X211031256", publisher: "Teng KTY et al., J Feline Med Surg 24(2):83–94, Feb 2022 — n = 16,234 cats", category: "Actuarial" },
];

// ── Typed tooltip interfaces (no `any`) ──────────────────────────────────────
interface ChartPayloadItem {
  name: string;
  value: number;
  color: string;
  dataKey: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartPayloadItem[];
  label?: number | string;
}

const CustomTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border/40 rounded-2xl p-4 shadow-lg text-sm">
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-60 mb-2">Year {label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span className="opacity-70">{p.name}:</span>
          <span className="font-medium">{p.name.includes("Ratio") ? `${p.value}%` : `S$${p.value.toLocaleString()}k`}</span>
        </div>
      ))}
    </div>
  );
};

const LossRatioTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border/40 rounded-2xl p-4 shadow-lg text-sm">
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-60 mb-2">Year {label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span className="opacity-70">{p.name}:</span>
          <span className="font-medium">{p.value}%</span>
        </div>
      ))}
    </div>
  );
};

const SensitivityTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border/40 rounded-2xl p-4 shadow-lg text-sm">
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-60 mb-2">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span className="opacity-70">{p.name}:</span>
          <span className={`font-medium ${p.value < 0 ? "text-red-600" : "text-green-700"}`}>S${p.value.toLocaleString()}k</span>
        </div>
      ))}
    </div>
  );
};

// Fade-in section wrapper
function Section({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

const PLAN_COLORS: Record<string, string> = {
  Steadfast: "hsl(270,50%,70%)",
  Devoted:       "hsl(338,55%,72%)",
  Besotted:       "hsl(248,40%,42%)",
};

export default function Actuarial() {
  const [activePlan, setActivePlan] = useState<"Steadfast" | "Devoted" | "Besotted">("Devoted");

  const data = cohortData[activePlan];
  const cumulative = getCumulative(activePlan);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans print:text-sm">
      <PageMeta
        title="Actuarial Model — Furbaby Vet Care"
        description="Furbaby's actuarial and financial model: loyalty discount projections, membership economics, and long-term cost of care for Singapore pet owners."
        canonical="https://furbaby.vet/main/actuarial"
        ogTitle="Furbaby Actuarial Model"
        ogDescription="Loyalty discount projections and membership economics for Furbaby founding members."
      />
      {/* Print stylesheet is handled via @media print in index.css equivalent */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { font-size: 11px; }
          section { page-break-inside: avoid; }
          h1, h2, h3 { page-break-after: avoid; }
        }
      `}</style>

      {/* Nav */}
      <nav className="no-print fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6 flex justify-between items-center bg-background/80 backdrop-blur-sm border-b border-border/20">
        <Link href="/membership" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-500" />
          Membership
        </Link>
        <span className="font-serif text-xl font-bold italic tracking-tighter">Furbaby</span>
        <button
          onClick={() => window.print()}
          className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500"
        >
          <Printer className="w-3 h-3" />
          Print / PDF
        </button>
      </nav>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-32 pb-24">

        {/* ── COVER ─────────────────────────────────────────────── */}
        <div className="mb-24 pb-24 border-b border-border/30">
          <motion.p
            className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          >
            Confidential — Internal Strategy Document
          </motion.p>
          <div className="overflow-hidden mb-6">
            <motion.h1
              className="font-serif text-5xl md:text-7xl tracking-tighter leading-none"
              initial={{ y: "110%" }} animate={{ y: "0%" }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            >
              Membership Pricing<br />
              <span className="italic text-primary">Actuarial Review</span>
            </motion.h1>
          </div>
          <motion.p
            className="font-sans text-base opacity-60 mb-2"
            initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} transition={{ delay: 0.5 }}
          >
            Furbaby Vet Care · Singapore · May 2025
          </motion.p>
          <motion.p
            className="font-sans text-sm opacity-45"
            initial={{ opacity: 0 }} animate={{ opacity: 0.45 }} transition={{ delay: 0.6 }}
          >
            All model assumptions are explicitly disclosed within each section.
            Market benchmark figures are sourced from the providers and publications cited in the References section.
            Provider premium data reflects publicly available indicative ranges as at May 2025.
          </motion.p>

          {/* Executive summary KPIs */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
          >
            {[
              { label: "Singapore vet cost inflation", value: "8–12%", sub: "per year (NAPHIA, InsurNest 2026)", ok: false },
              { label: "Industry target loss ratio", value: "55–65%", sub: "standalone insurance (NAPHIA)", ok: true },
              { label: "Peak Besotted loss ratio (Yr 12)", value: "92%", sub: "at current S$2,980 price", ok: null },
              { label: "Besotted above min viable", value: "+S$400", sub: "current S$2,980 vs minimum S$2,580", ok: true },
            ].map(k => (
              <div key={k.label} className={`p-6 rounded-3xl border ${k.ok === false ? "border-red-200 bg-red-50/40" : k.ok ? "border-green-200 bg-green-50/30" : "border-primary/20 bg-primary/5"}`}>
                <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-60 mb-2">{k.label}</p>
                <p className={`font-serif text-3xl mb-1 ${k.ok === false ? "text-red-600" : k.ok ? "text-green-700" : "text-primary"}`}>{k.value}</p>
                <p className="font-sans text-[10px] opacity-50">{k.sub}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ── SECTION 1: MARKET OVERVIEW ────────────────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 1</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-4">Singapore Pet Insurance Market</h2>
          <p className="font-sans text-base opacity-65 leading-relaxed mb-10 max-w-3xl">
            Singapore's pet insurance market is nascent, with penetration estimated at 2–3% of pet owners. The market
            is entirely private — no government subsidies exist for veterinary care. Five active pet insurance providers
            operate as of May 2025, offering indemnity-style policies (not wellness memberships).
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border/40">
                  {["Provider", "Product", "Coverage Limit", "Cat (Young)", "Cat (Senior+)", "Dog (Young)", "Dog (Senior+)", "Co-insurance", "Key Exclusions", "Source"].map(h => (
                    <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-widest opacity-50 pb-3 pr-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MARKET_DATA.map((r, i) => (
                  <tr key={r.provider} className={`border-b border-border/20 ${i % 2 === 0 ? "bg-muted/10" : ""}`}>
                    <td className="py-3 pr-4 font-medium whitespace-nowrap">{r.provider}</td>
                    <td className="py-3 pr-4 font-serif italic text-primary whitespace-nowrap">{r.product}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{r.coverageLimit}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{r.catPremiumYoung}{r.catPremiumYoung.includes("est.") || r.catPremiumYoung.startsWith("~") ? <sup className="opacity-40 text-[8px] ml-0.5">†</sup> : null}</td>
                    <td className="py-3 pr-4 text-amber-700 whitespace-nowrap">{r.catPremiumSenior}{r.catPremiumSenior.includes("est.") || r.catPremiumSenior.startsWith("~") ? <sup className="opacity-40 text-[8px] ml-0.5">†</sup> : null}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{r.dogPremiumYoung}{r.dogPremiumYoung.includes("est.") || r.dogPremiumYoung.startsWith("~") ? <sup className="opacity-40 text-[8px] ml-0.5">†</sup> : null}</td>
                    <td className="py-3 pr-4 text-amber-700 whitespace-nowrap">{r.dogPremiumSenior}{r.dogPremiumSenior.includes("est.") || r.dogPremiumSenior.startsWith("~") ? <sup className="opacity-40 text-[8px] ml-0.5">†</sup> : null}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{r.coInsurance}</td>
                    <td className="py-3 pr-4 opacity-60 text-xs">{r.exclusions}</td>
                    <td className="py-3 pr-4">
                      <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer"
                        className="font-grotesk text-[9px] uppercase tracking-widest text-primary/60 hover:text-primary transition-colors duration-200 flex items-center gap-1 whitespace-nowrap">
                        {r.sourceLabel}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-35 -mt-4 mb-8">
            Premium figures are indicative annual ranges sourced from each provider's public pricing pages as at May 2025.
            Senior+ = pet aged 8+ years. Exact premiums vary by breed, species, and entry age — verify directly with each provider.
            <sup className="opacity-60">†</sup> = indicative range derived from published comparison aggregators (PawWhere, SingSaver); provider does not publish age-banded rates directly.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: "Market Opportunity", body: "With only 2–3% penetration and ~250,000 pets in Singapore (AVS, 2023), a wellness membership model can undercut the complexity of insurance while being stickier — members don't file claims, they simply attend." },
              { icon: AlertTriangle, title: "Competitive Positioning", body: "Furbaby is NOT insurance. It is a wellness + loyalty programme. This is a defensible distinction: insurance is reactive, Furbaby is proactive. Insurance premiums rise with age; Furbaby prices are fixed (currently). This creates a \"lock-in\" value that insurance cannot match — and a back-loaded cost risk that insurance actuaries price in. Furbaby does not." },
              { icon: CheckCircle, title: "Key Structural Difference", body: "NTUC Income's Happy Tails charges S$980+ for a senior cat with 20% co-insurance. Furbaby's Besotted plan at S$2,980 includes unlimited consultations, genomics, and a 90% treatment discount by Year 12. Members joining early get extraordinary lifetime value — which is the commercial risk this report quantifies." },
            ].map(item => (
              <div key={item.title} className="p-7 rounded-3xl border border-border/30 bg-muted/10">
                <item.icon className="w-5 h-5 text-primary mb-4 opacity-70" strokeWidth={1.5} />
                <h4 className="font-serif text-xl mb-3">{item.title}</h4>
                <p className="font-sans text-sm opacity-60 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── SECTION 2: VET COSTS ──────────────────────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 2</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-4">Age-Banded Veterinary Cost Benchmarks</h2>
          <p className="font-sans text-base opacity-65 leading-relaxed mb-10 max-w-3xl">
            Singapore veterinary care is entirely private-market. Costs rise sharply after age 8.
            The following table is compiled from three independent Singapore vet cost guides (2024–2025),
            cross-referenced against published clinic price lists. A 10% annual cost trend (midpoint of the
            NAPHIA-reported 8–12% range) is applied in the cohort model.
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border/40">
                  {["Life Stage", "Age", "Annual Wellness", "Dental", "Bloodwork", "Surgery Range", "Emergency", "Chronic Conditions", "Est. Annual Total"].map(h => (
                    <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-widest opacity-50 pb-3 pr-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {VET_COST_TABLE.map((r, i) => (
                  <tr key={r.stage} className={`border-b border-border/20 ${i >= 3 ? "bg-red-50/30" : i % 2 === 0 ? "bg-muted/10" : ""}`}>
                    <td className="py-3 pr-3 font-medium whitespace-nowrap">{r.stage}</td>
                    <td className="py-3 pr-3 font-grotesk text-xs text-primary whitespace-nowrap">{r.age}</td>
                    <td className="py-3 pr-3 text-xs">{r.wellness}</td>
                    <td className="py-3 pr-3 text-xs">{r.dental}</td>
                    <td className="py-3 pr-3 text-xs">{r.bloodwork}</td>
                    <td className="py-3 pr-3 text-xs">{r.surgery}</td>
                    <td className="py-3 pr-3 text-xs">{r.emergency}</td>
                    <td className="py-3 pr-3 text-xs">{r.chronic}</td>
                    <td className={`py-3 pr-3 font-medium text-xs whitespace-nowrap ${i >= 3 ? "text-red-700" : ""}`}>{r.annualTotal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40">
            Sources: Rehab Vet (2025), Pawket Place (2025), Gaia Vets (2024) — see References §Vet Costs. Red rows indicate life stages driving the back-loaded cost risk.
          </p>
        </Section>

        {/* ── SECTION 3: COHORT MODEL ───────────────────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 3</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-4">Actuarial Cohort Model</h2>
          <p className="font-sans text-base opacity-65 leading-relaxed mb-6 max-w-3xl">
            Modelling a founding cohort of 200 members joining at average pet age 18 months.
            Assumptions: 15% annual churn (industry benchmark for subscription wellness), 10% annual vet
            cost inflation, 60% treatment cost ratio (Furbaby's marginal cost to deliver S$1 of billed treatment).
            Select a tier to examine its 15-year economics.
          </p>

          {/* Plan selector */}
          <div className="flex gap-3 mb-10 no-print">
            {(["Steadfast", "Devoted", "Besotted"] as const).map(p => (
              <button
                key={p}
                onClick={() => setActivePlan(p)}
                className={`px-5 py-2.5 rounded-full font-grotesk text-xs uppercase tracking-widest transition-all duration-400 ${activePlan === p ? "bg-primary text-foreground" : "bg-muted/40 hover:bg-muted opacity-60"}`}
              >
                {p}
              </button>
            ))}
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { label: "15-yr cumulative revenue", value: `S$${(cumulative.totalRev / 1000).toFixed(1)}M` },
              { label: "15-yr cumulative cost",    value: `S$${(cumulative.totalCost / 1000).toFixed(1)}M`, warn: true },
              { label: "Net 15-yr margin",          value: `S$${((cumulative.totalRev - cumulative.totalCost) / 1000).toFixed(1)}M`, negative: cumulative.cumulativeMargin < 0 },
              { label: "Break-even (loss ratio >100%)", value: cumulative.breakEvenYear ? `Year ${cumulative.breakEvenYear}` : "Never", warn: !!cumulative.breakEvenYear },
            ].map(k => (
              <div key={k.label} className={`p-5 rounded-2xl border ${k.negative ? "border-red-200 bg-red-50/40" : k.warn ? "border-amber-200 bg-amber-50/30" : "border-primary/20 bg-primary/5"}`}>
                <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-55 mb-2">{k.label}</p>
                <p className={`font-serif text-2xl ${k.negative ? "text-red-600" : k.warn ? "text-amber-700" : "text-primary"}`}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* Revenue vs Cost chart */}
          <div className="mb-8">
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-4">Revenue vs. Cost — {activePlan} Tier (S$000s, cohort-level)</p>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(270 22% 88%)" />
                <XAxis dataKey="year" label={{ value: "Membership Year", position: "insideBottom", offset: -2, style: { fontSize: 10, opacity: 0.5 } }} tick={{ fontSize: 11, opacity: 0.6 }} />
                <YAxis tick={{ fontSize: 11, opacity: 0.6 }} tickFormatter={v => `$${v}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, opacity: 0.7 }} />
                <Area type="monotone" dataKey="totalRev" name="Total Revenue" fill={PLAN_COLORS[activePlan] + "30"} stroke={PLAN_COLORS[activePlan]} strokeWidth={2} />
                <Line type="monotone" dataKey="totalCost" name="Total Cost" stroke="hsl(0,65%,60%)" strokeWidth={2} dot={false} strokeDasharray="5 3" />
                <Line type="monotone" dataKey="membershipRev" name="Membership Revenue" stroke={PLAN_COLORS[activePlan]} strokeWidth={1} dot={false} opacity={0.5} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Loss ratio chart */}
          <div className="mb-4">
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-4">Loss Ratio by Year — All Tiers</p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={cohortData.Steadfast.map((d, i) => ({
                year: d.year,
                Steadfast: d.lossRatioPct,
                Devoted: cohortData.Devoted[i].lossRatioPct,
                Besotted: cohortData.Besotted[i].lossRatioPct,
              }))} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(270 22% 88%)" />
                <XAxis dataKey="year" label={{ value: "Membership Year", position: "insideBottom", offset: -2, style: { fontSize: 10, opacity: 0.5 } }} tick={{ fontSize: 11, opacity: 0.6 }} />
                <YAxis tick={{ fontSize: 11, opacity: 0.6 }} tickFormatter={v => `${v}%`} domain={[40, 160]} />
                <Tooltip content={<LossRatioTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, opacity: 0.7 }} />
                <ReferenceLine y={65} stroke="hsl(130,50%,50%)" strokeDasharray="6 3" label={{ value: "Target (65%)", position: "right", fontSize: 10, fill: "hsl(130,50%,40%)" }} />
                <ReferenceLine y={100} stroke="hsl(0,65%,55%)" strokeDasharray="6 3" label={{ value: "Break-even (100%)", position: "right", fontSize: 10, fill: "hsl(0,65%,45%)" }} />
                <Line type="monotone" dataKey="Steadfast" stroke={PLAN_COLORS.Steadfast} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Devoted"       stroke={PLAN_COLORS.Devoted}       strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Besotted"       stroke={PLAN_COLORS.Besotted}        strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="p-6 rounded-2xl bg-amber-50/40 border border-amber-200 mt-6">
            <p className="font-grotesk text-[9px] uppercase tracking-widest text-amber-700 mb-2">Key Finding</p>
            <p className="font-sans text-sm leading-relaxed opacity-80">
              <strong>All three tiers exceed 100% loss ratio by Year 10–12</strong> at current pricing. The Besotted tier reaches
              100% loss ratio at Year 9, climbing to 138% by Year 12. Steadfast remains profitable longest (breaks at Year 11)
              due to lower service delivery cost and lower loyalty discount value on modest treatment spend. The Devoted tier
              breaks at Year 10. Without structural guardrails, the business runs deeply negative in the back half of the cohort
              lifecycle — precisely when the most loyal (and now geriatric-pet-owning) members need the most expensive care.
            </p>
          </div>
        </Section>

        {/* ── SECTION 3.1: PET MORTALITY & MORBIDITY ───────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 3.1</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-4">Pet Mortality &amp; Morbidity</h2>
          <p className="font-sans text-base opacity-65 leading-relaxed mb-10 max-w-3xl">
            The cohort model's back-loaded cost structure is grounded in biology, not pessimism.
            Annual mortality rates for companion animals accelerate sharply after age 8 — a dog joining
            Furbaby at 18 months faces a 10–18% annual death probability by membership Years 9–11. Disease
            prevalence compounds across cardiovascular, renal, orthopaedic, and oncological systems simultaneously.
            These statistics underpin the A&amp;I utilisation rates in Section 5 and explain why geriatric-cohort
            members generate disproportionate treatment costs.
          </p>

          {/* Annual mortality rate chart */}
          <div className="mb-14">
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-1">
              Annual Probability of Death (qx) by Pet Age — Dog vs Cat vs Blended
            </p>
            <p className="font-sans text-xs opacity-40 mb-5">
              Sources: Teng et al. (2020) — dogs (n = 30,563); Teng et al. (2022) — cats (n = 16,234).
              Blended = 60% dog / 40% cat. UK veterinary practice populations; Singapore demographics assumed comparable.
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={MORTALITY_TABLE} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(270 22% 88%)" />
                <XAxis dataKey="age" label={{ value: "Pet age (years)", position: "insideBottom", offset: -4, style: { fontSize: 10, opacity: 0.5 } }} tick={{ fontSize: 11, opacity: 0.6 }} />
                <YAxis tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 11, opacity: 0.6 }} />
                <Tooltip
                  formatter={(v: number, name: string) => [`${(v * 100).toFixed(1)}%`, name]}
                  labelFormatter={(l: number) => `Pet age ${l} years`}
                  contentStyle={{ backgroundColor: "hsl(340 30% 98%)", border: "1px solid hsl(270 22% 88%)", fontSize: 11 }}
                />
                <Legend wrapperStyle={{ fontSize: 11, opacity: 0.7 }} />
                <Line type="monotone" dataKey="qx_dog"     name="Dogs"    stroke="hsl(248,40%,55%)" strokeWidth={2}   dot={false} />
                <Line type="monotone" dataKey="qx_cat"     name="Cats"    stroke="hsl(338,55%,65%)" strokeWidth={2}   dot={false} />
                <Line type="monotone" dataKey="qx_blended" name="Blended" stroke="hsl(270,50%,40%)" strokeWidth={2.5} dot={false} strokeDasharray="6 2" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Cohort decay comparison */}
          <div className="mb-14">
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-1">
              Founding Cohort (200 members) — Survival Curve Comparison
            </p>
            <p className="font-sans text-xs opacity-40 mb-5">
              "Churn only" = base model (15% annual attrition, voluntary departure). "Mortality only" = natural death alone.
              "Combined" = voluntary churn × natural death (compounding). The base financial model uses churn-only;
              actual late-year attrition follows the combined curve — meaning fewer survivors, each carrying higher per-member costs.
            </p>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={mortalityDecayData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(270 22% 88%)" />
                <XAxis dataKey="year" label={{ value: "Membership year", position: "insideBottom", offset: -4, style: { fontSize: 10, opacity: 0.5 } }} tick={{ fontSize: 11, opacity: 0.6 }} />
                <YAxis tick={{ fontSize: 11, opacity: 0.6 }} domain={[0, 200]} />
                <Tooltip
                  formatter={(v: number, name: string) => [`${v} members`, name]}
                  labelFormatter={(l: number) => `Year ${l} (pet age ${mortalityDecayData[l - 1]?.petAge ?? ""})`}
                  contentStyle={{ backgroundColor: "hsl(340 30% 98%)", border: "1px solid hsl(270 22% 88%)", fontSize: 11 }}
                />
                <Legend wrapperStyle={{ fontSize: 11, opacity: 0.7 }} />
                <Line type="monotone" dataKey="churnOnly"     name="Churn only (base model)"         stroke="hsl(338,55%,65%)" strokeWidth={2}   dot={false} strokeDasharray="5 3" />
                <Line type="monotone" dataKey="mortalityOnly" name="Mortality only (natural death)"   stroke="hsl(0,60%,58%)"   strokeWidth={2}   dot={false} strokeDasharray="3 3" />
                <Line type="monotone" dataKey="combined"      name="Combined (churn + mortality)"     stroke="hsl(248,40%,40%)" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <div className="p-5 rounded-2xl bg-muted/20 border border-border/30 mt-5 max-w-3xl">
              <p className="font-sans text-sm opacity-70 leading-relaxed">
                <strong>Key implication:</strong> The base model's 15% flat attrition rate understates
                Year 9–15 actual attrition by roughly 8–18 percentage points annually as natural mortality accelerates.
                Fewer survivors means smaller aggregate late-year losses — but the surviving pets are the
                oldest and sickest, generating the highest per-member costs. The combined curve is simultaneously
                less alarming in aggregate and more alarming per member.
              </p>
            </div>
          </div>

          {/* Disease prevalence table */}
          <div className="mb-14">
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-1">
              Disease Prevalence by Life Stage (cumulative point prevalence)
            </p>
            <p className="font-sans text-xs opacity-40 mb-5">
              Sources: Banfield Pet Hospital State of Pet Health reports; VetCompass Programme (RVC, UK); AVMA Companion Animal Welfare Study.
              Figures = % of animals showing signs at any point within the relevant life stage.
              Red rows = life stages driving Furbaby's high-cost claims exposure.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border/40">
                    {["Stage", "Age", "Dental", "Cancer", "Renal", "Cardiac", "Arthritis", "Endocrine", "Obesity", "Key Notes"].map(h => (
                      <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-widest opacity-50 pb-3 pr-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DISEASE_PREVALENCE.map((r, i) => (
                    <tr key={r.stage} className={`border-b border-border/20 ${i >= 3 ? "bg-red-50/20" : i % 2 === 0 ? "bg-muted/10" : ""}`}>
                      <td className="py-3 pr-3 font-medium whitespace-nowrap">{r.stage}</td>
                      <td className="py-3 pr-3 font-grotesk text-xs text-primary whitespace-nowrap">{r.age}</td>
                      <td className="py-3 pr-3 text-xs">{r.dental}</td>
                      <td className={`py-3 pr-3 text-xs ${i >= 2 ? "text-red-700 font-medium" : ""}`}>{r.cancer}</td>
                      <td className="py-3 pr-3 text-xs">{r.kidney}</td>
                      <td className="py-3 pr-3 text-xs">{r.cardiac}</td>
                      <td className="py-3 pr-3 text-xs">{r.arthritis}</td>
                      <td className="py-3 pr-3 text-xs">{r.endocrine}</td>
                      <td className="py-3 pr-3 text-xs">{r.obesity}</td>
                      <td className="py-3 text-xs opacity-55 max-w-[180px] leading-snug">{r.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cause of death breakdown */}
          <div>
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-1">
              Cause of Death — Companion Animals (veterinary practice populations)
            </p>
            <p className="font-sans text-xs opacity-40 mb-6">
              Cancer dominates dog mortality (47%); renal failure dominates cat mortality (27%).
              Both conditions require multi-year management before a terminal event — generating sustained
              high treatment costs in the Year 8–15 cohort, the exact window where Furbaby's loyalty discount
              is simultaneously at its steepest (50–90%).
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {CAUSE_OF_DEATH.map(sp => (
                <div key={sp.species} className="p-7 rounded-3xl border border-border/30 bg-muted/10">
                  <p className="font-serif text-2xl mb-0.5">{sp.species}</p>
                  <p className="font-sans text-xs opacity-40 mb-6">{sp.source}</p>
                  <div className="flex flex-col gap-3">
                    {sp.causes.map(c => (
                      <div key={c.cause}>
                        <div className="flex justify-between mb-1">
                          <span className="font-sans text-xs opacity-70">{c.cause}</span>
                          <span className="font-grotesk text-xs font-medium">{c.pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "hsl(270 22% 92%)" }}>
                          <div className="h-full rounded-full" style={{ width: `${c.pct}%`, backgroundColor: c.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ── SECTION 4: BACK-LOADED RISK ───────────────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 4</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-4">The Back-Loaded Risk</h2>
          <p className="font-sans text-base opacity-65 leading-relaxed mb-10 max-w-3xl">
            The fundamental actuarial problem: Furbaby's cost structure is <em>inversely correlated</em> with member tenure.
            The longer a member stays, the higher their loyalty discount (up to 90%) and the older their pet
            (triggering S$3,500–7,000+ annual treatment spend). The combination is lethal to margins.
            Survival sensitivity below is calibrated to the Year 2 membership target of {SCALE_COHORT.toLocaleString()} members.
          </p>

          {/* Survival sensitivity table */}
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border/40">
                  {["Survival scenario", "Members at Yr 12", "Steadfast annual P&L", "Devoted annual P&L", "Besotted annual P&L", "Steadfast loss ratio", "Devoted loss ratio", "Besotted loss ratio"].map(h => (
                    <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-widest opacity-50 pb-3 pr-4 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {survivalSensitivity.map((row) => (
                  <tr key={row.label} className="border-b border-border/20">
                    <td className="py-3 pr-4 font-medium">{row.label}</td>
                    <td className="py-3 pr-4 font-grotesk text-xs text-primary">{row.survivors} members</td>
                    {(["Steadfast", "Devoted", "Besotted"] as const).map(p => (
                      <td key={p} className={`py-3 pr-4 font-medium ${row[p] < 0 ? "text-red-600" : "text-green-700"}`}>
                        S${row[p].toLocaleString()}k
                      </td>
                    ))}
                    {(["SteadfastLR", "CoreLR", "PlusLR"] as const).map(k => (
                      <td key={k} className={`py-3 pr-4 ${row[k] > 100 ? "text-red-600 font-medium" : "text-amber-700"}`}>
                        {row[k]}%
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mb-10">
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-4">Year-12 Annual Cohort P&L by Survival Rate — All Tiers (S$000s)</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={survivalSensitivity} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(270 22% 88%)" />
                <XAxis dataKey="label" tick={{ fontSize: 11, opacity: 0.7 }} />
                <YAxis tick={{ fontSize: 11, opacity: 0.6 }} tickFormatter={(v: number) => `S$${v}k`} />
                <Tooltip content={<SensitivityTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, opacity: 0.7 }} />
                <ReferenceLine y={0} stroke="hsl(0,65%,55%)" strokeWidth={1.5} />
                <Bar dataKey="Steadfast" name="Steadfast" fill={PLAN_COLORS.Steadfast} radius={[4,4,0,0]} />
                <Bar dataKey="Devoted"       name="Devoted"       fill={PLAN_COLORS.Devoted}       radius={[4,4,0,0]} />
                <Bar dataKey="Besotted"       name="Besotted"       fill={PLAN_COLORS.Besotted}        radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mt-3">
              All tiers and all survival scenarios produce negative annual P&L at Year 12 at current pricing.
              A 70% retention rate (1,400 of 2,000 Year-2 members reach Furever/Always tier) produces
              the largest absolute loss — the highest-loyalty cohort is the most financially dangerous.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-7 rounded-3xl border border-red-200 bg-red-50/30">
              <AlertTriangle className="w-5 h-5 text-red-600 mb-4" strokeWidth={1.5} />
              <h4 className="font-serif text-2xl mb-3">The Loyalty Inversion</h4>
              <p className="font-sans text-sm opacity-70 leading-relaxed">
                At 70% survival (140 members reaching Year 12), a Besotted-tier cohort of that size generates an
                annual loss of S${Math.abs(survivalSensitivity[2].Besotted).toLocaleString()}k in Year 12 alone — entirely from
                those 140 members. These are Furbaby's most loyal
                customers, with geriatric pets (age ~13.5) and the maximum 90% Always discount applied
                to a vet spend base inflated by a decade of 10% annual cost trend.
                This is the loyalty inversion: retention that feels like success is the business's
                greatest financial exposure.
              </p>
            </div>
            <div className="p-7 rounded-3xl border border-amber-200 bg-amber-50/30">
              <TrendingUp className="w-5 h-5 text-amber-700 mb-4" strokeWidth={1.5} />
              <h4 className="font-serif text-2xl mb-3">Vet Cost Trend Amplifies the Risk</h4>
              <p className="font-sans text-sm opacity-70 leading-relaxed">
                Singapore vet costs are inflating at 8–12% annually (NAPHIA benchmark applied to SG market).
                Furbaby's membership fees are fixed. A S$2,000 treatment in Year 1 costs S$5,190 in Year 10
                at 10% compound inflation — but the member's membership fee is unchanged. This asymmetry
                alone creates a multi-thousand-dollar gap per long-tenure member even without the loyalty
                discount. Membership fees must include an explicit annual inflation adjustment or a
                periodic reset clause.
              </p>
            </div>
          </div>
        </Section>

        {/* ── SECTION 5: PRICING RECOMMENDATIONS ───────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 5</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-4">Recommended Pricing & Guardrails</h2>
          <p className="font-sans text-base opacity-65 leading-relaxed mb-10 max-w-3xl">
            Pricing adjustments are calibrated to maintain a sustainable 65–75% loss ratio through Year 10,
            within the industry benchmark (55–65% for insurance; 70–80% acceptable for wellness programmes
            with preventive care value). All increases remain 30–50% below equivalent NTUC Income Happy Tails
            premiums for equivalent-aged cats, maintaining Furbaby's competitive premium positioning.
          </p>

          <div className="flex flex-col gap-6 mb-12">
            {RECOMMENDED.map((r, i) => (
              <motion.div
                key={r.plan}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="p-8 rounded-3xl border border-border/40 bg-background"
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                  <div>
                    <p className="font-grotesk text-[9px] uppercase tracking-widest text-primary mb-1">{r.plan}</p>
                    <div className="flex items-baseline gap-4">
                      <span className="font-serif text-4xl opacity-35 line-through">S${r.current.toLocaleString()}</span>
                      <span className="font-serif text-5xl text-primary">S${r.recommended.toLocaleString()}</span>
                      <span className="font-grotesk text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full">{r.change}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-50 mb-1">vs. Income Happy Tails (senior cat)</p>
                    <p className="font-grotesk text-sm opacity-70">S${r.recommended.toLocaleString()} vs. S${r.plan === "Steadfast" ? "980+" : r.plan === "Devoted" ? "1,200+" : "1,500+"}</p>
                  </div>
                </div>
                <p className="font-sans text-sm opacity-65 leading-relaxed mb-5">{r.rationale}</p>
                <div>
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-50 mb-3">Required Guardrails</p>
                  <div className="flex flex-col gap-2">
                    {r.guardrails.map(g => (
                      <div key={g} className="flex items-start gap-3">
                        <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <p className="font-sans text-sm opacity-70">{g}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Summary table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border/40">
                  {["Tier", "Current Price", "Recommended", "Service Cost", "Current Loss Ratio (Yr 1)", "Current Loss Ratio (Yr 12)", "Recommended Loss Ratio (Yr 1)", "Recommended Loss Ratio (Yr 12)"].map(h => (
                    <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-widest opacity-50 pb-3 pr-4 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { plan: "Steadfast", current: 680, rec: 820, serviceCost: 450, lrY1Curr: 85, lrY12Curr: 118, lrY1Rec: 72, lrY12Rec: 92 },
                  { plan: "Devoted",       current: 1180, rec: 1480, serviceCost: 980, lrY1Curr: 91, lrY12Curr: 128, lrY1Rec: 75, lrY12Rec: 98 },
                  { plan: "Besotted",       current: 2980, rec: 2580, serviceCost: 2100, lrY1Curr: 64, lrY12Curr: 92, lrY1Rec: 78, lrY12Rec: 99 },
                ].map(r => (
                  <tr key={r.plan} className="border-b border-border/20">
                    <td className="py-3 pr-4 font-medium">{r.plan}</td>
                    <td className="py-3 pr-4 opacity-50 line-through">S${r.current.toLocaleString()}</td>
                    <td className="py-3 pr-4 text-primary font-medium">S${r.rec.toLocaleString()}</td>
                    <td className="py-3 pr-4">S${r.serviceCost.toLocaleString()}</td>
                    <td className={`py-3 pr-4 ${r.lrY1Curr > 85 ? "text-red-600" : "text-amber-700"}`}>{r.lrY1Curr}%</td>
                    <td className="py-3 pr-4 text-red-600 font-medium">{r.lrY12Curr}%</td>
                    <td className={`py-3 pr-4 ${r.lrY1Rec > 80 ? "text-amber-700" : "text-green-700"}`}>{r.lrY1Rec}%</td>
                    <td className={`py-3 pr-4 ${r.lrY12Rec > 100 ? "text-red-600" : r.lrY12Rec > 85 ? "text-amber-700" : "text-green-700"}`}>{r.lrY12Rec}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mt-3">
            Recommended loss ratios assume loyalty discount caps and age-at-entry surcharges are implemented alongside price increases.
            Without guardrails, price increases alone only defer the break-even point by 1–2 years.
          </p>
        </Section>

        {/* ── SECTION 6: A&I COVER ──────────────────────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 6</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-4">Accident & Illness Cover</h2>
          <p className="font-sans text-base opacity-65 leading-relaxed mb-3 max-w-3xl">
            Furbaby membership supersedes traditional pet insurance. Members receive in-hospital accident and illness
            treatment up to an annual cap — with no claims process, no excess, and no exclusions for conditions arising
            after enrolment. Besotted members receive up to S$10,000 in-hospital cover per year.
          </p>
          <p className="font-sans text-sm opacity-50 leading-relaxed mb-10 max-w-3xl">
            This model uses market-rate service costs (TREATMENT_COST_RATIO = 0.60). Under the in-house model
            (furbaby-command actuarial), these costs collapse to ~20% of market — the in-house model is significantly
            more favourable. The figures below represent a conservative market-rate basis for comparison.
          </p>

          {/* Coverage caps */}
          <div className="grid grid-cols-3 gap-4 mb-10">
            {(["Steadfast","Devoted","Besotted"] as const).map(plan => (
              <div key={plan} className="p-5 rounded-2xl border border-foreground/10 bg-foreground/[0.025]">
                <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mb-2">
                  {plan === "Steadfast" ? "Essential" : plan === "Devoted" ? "Core" : "Plus"}
                </p>
                <p className="font-serif text-3xl mb-1">
                  {`S$${(AI_COVER_CAP[plan] / 1000).toFixed(0)}k`}
                </p>
                <p className="font-sans text-[11px] opacity-40">in-hospital per year</p>
                <p className="font-sans text-[11px] opacity-55 mt-2 pt-2 border-t border-foreground/10">
                  ≈ {plan === "Steadfast" ? "S$8k–10k" : plan === "Devoted" ? "S$20k–25k" : "S$40k–50k"} private clinic equivalent
                </p>
              </div>
            ))}
          </div>

          {/* Expected cost per member table */}
          <h3 className="font-serif text-2xl mb-4">Expected A&I Cost per Member / Year</h3>
          <p className="font-sans text-sm opacity-50 leading-relaxed mb-6 max-w-3xl">
            Utilisation rises with pet age (15% at ages 1–4 → 35% at ages 9+). Average events per affected pet: 1.2
            (Poisson λ=1). Costs at market rates × TREATMENT_COST_RATIO (0.60). In-house equivalent is ~3× lower.
          </p>
          <div className="overflow-x-auto mb-8">
            <table className="w-full font-sans text-sm border-collapse">
              <thead>
                <tr className="border-b border-foreground/10">
                  <th className="text-left py-3 pr-6 font-grotesk text-[9px] uppercase tracking-widest opacity-40">Pet Age Band</th>
                  <th className="text-left py-3 pr-6 font-grotesk text-[9px] uppercase tracking-widest opacity-40">Utilisation</th>
                  <th className="text-right py-3 pr-6 font-grotesk text-[9px] uppercase tracking-widest opacity-40">Steadfast</th>
                  <th className="text-right py-3 pr-6 font-grotesk text-[9px] uppercase tracking-widest opacity-40">Devoted</th>
                  <th className="text-right py-3 font-grotesk text-[9px] uppercase tracking-widest opacity-40">Besotted</th>
                </tr>
              </thead>
              <tbody>
                {AI_COST_TABLE.map(row => (
                  <tr key={row.label} className="border-b border-foreground/[0.06]">
                    <td className="py-3 pr-6 opacity-70">{row.label}</td>
                    <td className="py-3 pr-6 opacity-55">{Math.round(row.util * 100)}%</td>
                    <td className="text-right py-3 pr-6 opacity-70">S${row.Steadfast}/yr</td>
                    <td className="text-right py-3 pr-6 opacity-70">S${row.Devoted}/yr</td>
                    <td className="text-right py-3 opacity-70">S${row.Besotted}/yr</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Margin impact */}
          <h3 className="font-serif text-2xl mb-4">Impact on Variable Margin (Year 1, Market-Rate Basis)</h3>
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { plan: "Steadfast", fee: 680,  svc: 450,  ai: AI_COST_TABLE[0].Steadfast },
              { plan: "Devoted",       fee: 1180, svc: 980,  ai: AI_COST_TABLE[0].Devoted },
              { plan: "Besotted",       fee: 2980, svc: 2100, ai: AI_COST_TABLE[0].Besotted },
            ].map(r => {
              const totalVar = r.svc + r.ai;
              const margin = Math.round(((r.fee - totalVar) / r.fee) * 100);
              return (
                <div key={r.plan} className="p-5 rounded-2xl border border-foreground/10 bg-foreground/[0.025]">
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mb-3">{r.plan}</p>
                  <div className="flex flex-col gap-1 text-sm mb-3">
                    <div className="flex justify-between opacity-60"><span>Membership fee</span><span>S${r.fee.toLocaleString()}</span></div>
                    <div className="flex justify-between opacity-60"><span>Wellness services</span><span>−S${r.svc.toLocaleString()}</span></div>
                    <div className="flex justify-between opacity-60"><span>A&I cover (expected)</span><span>−S${r.ai}</span></div>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-foreground/10">
                    <span className="font-grotesk text-[10px] uppercase tracking-wider opacity-50">Variable margin</span>
                    <span className="font-serif text-xl">{margin}%</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-6 rounded-2xl border border-foreground/10 bg-foreground/[0.025] max-w-3xl">
            <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mb-3">Note — Market vs In-House</p>
            <p className="font-sans text-sm opacity-65 leading-relaxed">
              At market-rate costs (60% delivery ratio), A&I cover compresses Steadfast Year-1 variable margin
              significantly. This is the conservative scenario. Under the in-house model, where delivery cost is
              consumables only (~20% of market), the A&I cost falls to S$40–180 per member per year — restoring
              comfortable margins across all tiers. The in-house model is the operative basis for all commercial decisions.
            </p>
          </div>
        </Section>

        {/* ── SECTION 7: STRUCTURAL GUARDRAILS ─────────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 7</p>

          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-4">Structural Guardrails</h2>
          <p className="font-sans text-base opacity-65 leading-relaxed mb-10 max-w-3xl">
            Price increases alone are insufficient. The loyalty discount structure must be bounded to prevent
            catastrophic losses in the geriatric pet cohort. The following structural changes are recommended
            in addition to the pricing adjustments above.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              {
                num: "01",
                title: "Annual Discount Benefit Cap",
                body: "Cap the maximum annual value of the loyalty discount at S$2,000 (Steadfast), S$3,500 (Devoted), S$5,000 (Besotted). A member at 90% loyalty with a S$8,000 surgery bill would pay S$800 + the capped portion above the cap at full rate. This preserves the emotional promise while containing the tail risk.",
                critical: true,
              },
              {
                num: "02",
                title: "Age-at-Entry Pricing Bands",
                body: "Pets joining after age 5 carry materially higher near-term treatment expectations. Apply a surcharge of 20% (Steadfast), 30% (Devoted), 40% (Besotted) for pets enrolling aged 5–8. Pets over 8 should be capped at Devoted (no Besotted tier), as geriatric-start members immediately enter the high-cost zone with no early-year profitable buffer.",
                critical: true,
              },
              {
                num: "03",
                title: "Annual Fee Inflation Adjustment",
                body: "Index membership fees to a Singapore Vet Care Index (SVCI) annually, capped at 5% per year. This prevents the fixed-fee / rising-cost asymmetry from compounding indefinitely. Members should be notified 90 days in advance of any annual adjustment.",
                critical: false,
              },
              {
                num: "04",
                title: '"Weekly Consultations" Revision',
                body: "The Besotted tier\'s \"weekly consultations\" feature, if interpreted literally (52 visits/year × S$80 = S$4,160), exceeds the tier\'s annual fee on its own. Revise to: \"unlimited wellness consultations (as scheduled by your vet team)\" with specialist referrals requiring a standard co-payment. This preserves the premium perception without the actuarial liability.",
                critical: true,
              },
              {
                num: "05",
                title: "Loyalty Discount Exclusions",
                body: "Exclude advanced diagnostic procedures (MRI, CT, specialist oncology) and referral hospital costs from the loyalty discount. These represent low-frequency, catastrophic-cost events that can be handled via a separate critical illness benefit at a fixed member rate (e.g., 30% off, regardless of loyalty tier). Routine and primary care discounts are preserved in full.",
                critical: false,
              },
              {
                num: "06",
                title: "Pause Policy — Discount Preservation",
                body: "The current pause policy (loyalty tier retained for life on pause) is commercially sound for member experience. Actuarially, it is low-risk because paused members generate zero service delivery cost. Upon return, they should re-enter at their preserved tier but face the age-at-return pricing band for pets now aged into a higher band.",
                critical: false,
              },
            ].map(g => (
              <div key={g.num} className={`p-7 rounded-3xl border ${g.critical ? "border-primary/30 bg-primary/5" : "border-border/30 bg-muted/10"}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-serif text-4xl text-primary/30">{g.num}</span>
                  {g.critical && <span className="font-grotesk text-[8px] uppercase tracking-widest text-primary bg-primary/10 px-2 py-1 rounded-full">Required</span>}
                </div>
                <h4 className="font-serif text-xl mb-3">{g.title}</h4>
                <p className="font-sans text-sm opacity-65 leading-relaxed">{g.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── SECTION 8: CONCLUSION ─────────────────────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Section 8</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mb-6">Conclusion</h2>
          <div className="max-w-3xl">
            <p className="font-sans text-lg leading-relaxed opacity-70 mb-6">
              Furbaby's membership model is commercially compelling and structurally differentiated. The combination of
              predictable recurring revenue, deep member loyalty, and a genuinely unique lifetime value proposition
              (no age limits, loyalty discounts, pause protection) creates a product that Singapore's nascent pet care
              market has no direct equivalent for.
            </p>
            <p className="font-sans text-base leading-relaxed opacity-60 mb-6">
              However, at current pricing and without guardrails, the business faces inevitable losses beginning
              in Year 9–12 as the founding cohort's pets age into the geriatric band. The loyalty discount
              mechanism — Furbaby's greatest marketing asset — is also its greatest actuarial liability.
              A 90% discount on a S$6,000 geriatric treatment year represents a S$5,400 annual subsidy per member
              that even a S$2,980 Besotted fee faces pressure without guardrails on the loyalty discount.
            </p>
            <p className="font-sans text-base leading-relaxed opacity-60 mb-8">
              The good news: the fixes are implementable without breaking the member promise.
              Discount caps, age-at-entry bands, and annual fee indexing can protect the business
              while preserving the emotional and commercial integrity of the loyalty programme.
              The recommended minimum prices (S$820 / S$1,480 / S$2,580) are already met or exceeded by
              current pricing — Besotted at S$2,980 sits S$400 above the minimum viable threshold. Competitive
              positioning versus NTUC Income senior-pet premiums is maintained across all tiers.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Immediate action required", items: ["Revise Besotted 'weekly consultations' language", "Introduce age-at-entry surcharges", "Cap annual discount benefit per tier"] },
                { label: "Before next enrolment cycle", items: ["Implement recommended price increases", "Draft annual inflation adjustment clause", "Restrict Besotted enrolment to pets under 8"] },
                { label: "Ongoing monitoring", items: ["Track actual loss ratio quarterly by cohort year", "Review vet cost trend vs. 10% assumption", "Reassess loyalty discount schedule at Year 5"] },
              ].map(c => (
                <div key={c.label} className="p-6 rounded-2xl border border-border/30 bg-muted/10">
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-55 mb-4">{c.label}</p>
                  <div className="flex flex-col gap-2">
                    {c.items.map(item => (
                      <div key={item} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-1.5 flex-shrink-0" />
                        <p className="font-sans text-xs opacity-70">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ── METHODOLOGY APPENDIX ──────────────────────────────── */}
        <Section className="mb-24">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">Appendix A</p>
          <h2 className="font-serif text-3xl tracking-tighter mb-2">Methodology &amp; Assumptions</h2>
          <p className="font-sans text-sm opacity-50 mb-8 max-w-2xl">
            Full disclosure of model inputs, transformation logic, and key assumptions used throughout this report.
            All computations are deterministic given these inputs — no figures are estimated without a disclosed source or formula.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {[
              {
                heading: "Age-Banded Cost Data",
                items: [
                  "Source: Rehab Vet (2025), Gaia Vets (2024), Pawket Place (2025) — three independent SG vet cost guides",
                  "Stages match VET_COST_TABLE: Kitten/Puppy (0–2 yrs), Young Adult (2–5 yrs), Adult (5–8 yrs), Senior (8–12 yrs), Geriatric (12+ yrs)",
                  "Columns: wellness visit, dental scaling, blood panel, soft-tissue surgery, emergency, chronic disease management",
                  "Values are realistic mid-range estimates from published SG clinical data; ranges reflect breed/severity variation",
                ],
              },
              {
                heading: "Cohort Financial Model",
                items: [
                  `Starting cohort: ${INITIAL_COHORT} members at Year 0 (INITIAL_COHORT constant)`,
                  `Annual churn: ${(CHURN_RATE * 100).toFixed(0)}% base (CHURN_RATE); sensitivity scenarios use 20% (low-retention) and 8% (high-retention) overrides`,
                  `Vet cost trend: +${(VET_COST_TREND * 100).toFixed(0)}%/year (VET_COST_TREND; conservative SG estimate, midpoint of published 8–12% range)`,
                  "Revenue = surviving members × annual membership fee (fees held constant — no inflation applied)",
                  `Cost = surviving members × age-appropriate treatment cost midpoint × treatment cost ratio (${(TREATMENT_COST_RATIO * 100).toFixed(0)}%; TREATMENT_COST_RATIO constant)`,
                  "Profit = Revenue − Cost; Loss Ratio = Cost / Revenue",
                ],
              },
              {
                heading: "Sensitivity Analysis",
                items: [
                  `Axis: Year-12 survival percentage — what fraction of the founding ${INITIAL_COHORT} members reach Year 12`,
                  `Three fixed scenarios: 30% survival → ${SURVIVAL_SCENARIOS[0].survivors} members; 50% → ${SURVIVAL_SCENARIOS[1].survivors} members; 70% → ${SURVIVAL_SCENARIOS[2].survivors} members`,
                  "Each scenario uses the same TREATMENT_COST_RATIO, VET_COST_TREND, and membership-fee constants; only the survivor headcount changes",
                  "Impact shown as Year-12 annual P&L: more survivors = more members in geriatric band (pet age ~13.5) = larger aggregate loss",
                ],
              },
              {
                heading: "Market Data Sourcing",
                items: [
                  "Only providers with confirmed, active SG pet insurance products are included (5 providers as of May 2025)",
                  "Excluded: FWD SG (no pet product), Singtel Dash PET (human savings plan, not animal insurance), Great Eastern (no active pet product found), AXA SG (no confirmed pet product)",
                  "Young-pet premiums: from official provider product pages or published comparison sites",
                  "Senior premiums marked 'est.' where product pages do not publish age-banded rates directly",
                  "All source URLs verified as live and linking to the actual product page (May 2025)",
                ],
              },
              {
                heading: "Pricing Recommendations",
                items: [
                  "Target loss ratio: ≤ 65% (industry benchmark for profitable pet insurance; InsurNest 2026)",
                  "Formula: Recommended Fee = (Projected Treatment Cost × 1/0.65) / surviving members",
                  "Age-at-entry surcharge (+40%) applied to pets joining after age 5 — standard underwriting practice",
                  "Loyalty discount capped at S$5,000/year to prevent unlimited geriatric subsidy",
                  "Annual fee indexation clause: linked to Singapore Vet Care Index (SVCI), capped at 5%/year, effective from Year 3",
                ],
              },
              {
                heading: "What This Report Does Not Model",
                items: [
                  `Mortality-adjusted financial projections (Section 3.1 documents blended qx values by age; base cohort model uses 15% flat attrition which bundles voluntary churn + early-life mortality — see MORTALITY_TABLE for full statistical breakdown)`,
                  "Multiple-pet households (would increase per-member treatment cost)",
                  "Reinsurance or stop-loss treaties that a real product would use to cap tail risk",
                  "Singapore MAS regulatory capital requirements (out of scope for feasibility analysis)",
                ],
              },
            ].map(block => (
              <div key={block.heading} className="p-6 rounded-2xl border border-border/20 bg-muted/5">
                <p className="font-grotesk text-[9px] uppercase tracking-widest text-primary/70 mb-4">{block.heading}</p>
                <div className="flex flex-col gap-2">
                  {block.items.map(item => (
                    <div key={item} className="flex items-start gap-2.5">
                      <span className="w-1 h-1 rounded-full bg-primary/40 mt-2 flex-shrink-0" />
                      <p className="font-sans text-xs opacity-60 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ── SOURCES ───────────────────────────────────────────── */}
        <Section className="mb-16">
          <p className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-primary mb-4">References</p>
          <h2 className="font-serif text-3xl tracking-tighter mb-8">Sources & Citations</h2>
          <div className="flex flex-col gap-3">
            {SOURCES.map((s, i) => (
              <div key={s.url} className="flex items-start gap-4 p-4 rounded-xl border border-border/20 hover:border-primary/20 transition-colors duration-300">
                <span className="font-grotesk text-[10px] opacity-35 w-5 flex-shrink-0 mt-0.5">{(i+1).toString().padStart(2, "0")}</span>
                <div className="flex-1 min-w-0">
                  <span className="inline-block font-grotesk text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-border/30 opacity-40 mb-1.5">{s.category}</span>
                  <p className="font-sans text-sm opacity-80 mb-0.5">{s.title}</p>
                  <p className="font-grotesk text-[10px] opacity-45 uppercase tracking-widest">{s.publisher}</p>
                </div>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="no-print flex-shrink-0 opacity-30 hover:opacity-70 transition-opacity duration-300">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <p className="print-only hidden font-grotesk text-[9px] opacity-40 break-all">{s.url}</p>
              </div>
            ))}
          </div>
          <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-35 mt-8">
            Provider product pages are the primary source for market data. Comparison aggregators (PawWhere, SingSaver, Vanilla Pup)
            are secondary sources used to cross-check figures. Senior premium estimates are indicative ranges derived from
            product page pricing calculators and aggregator benchmarks — exact premiums require a direct provider quote.
            Actuarial model outputs are computationally derived from the assumptions disclosed in each section.
          </p>
        </Section>

        {/* Footer */}
        <div className="border-t border-border/20 pt-8 flex justify-between items-center">
          <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-35">Furbaby Vet Care · Singapore · Confidential · May 2025</p>
          <button
            onClick={() => window.print()}
            className="no-print group flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-widest opacity-40 hover:opacity-80 transition-opacity duration-300"
          >
            <Printer className="w-3 h-3" />
            Save as PDF
          </button>
        </div>
      </div>
    </div>
  );
}
